import React from 'react';
import { Phone, KeyRound, MessageCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { ThemedInput } from '../../themed';
import { isInternationalNumber } from '../../../utils/phoneUtils';

/**
 * ContactVerificationSection Component
 *
 * Displays contact number, entity verification field (conditional), and special requests.
 * Verification field only shows if additionalVerificationEnabled is true.
 * Shows verification error message if validation fails.
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - Contact/verification values managed by parent component
 *
 * @param {Object} props
 * @param {string} props.contactNumber - Contact phone number
 * @param {string} props.specialRequests - Special requests text
 * @param {string} props.entityVerificationValue - Entity verification value
 * @param {Function} props.onChange - Handler for input changes
 * @param {boolean} props.additionalVerificationEnabled - Whether verification is enabled
 * @param {string} props.verificationPrompt - Translated verification prompt
 * @param {string} props.verificationError - Verification error message
 */
const ContactVerificationSection = ({
  contactNumber,
  specialRequests,
  entityVerificationValue,
  onChange,
  additionalVerificationEnabled,
  verificationPrompt,
  verificationError
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();

  return (
    <>
      {/* Contact Number */}
      <div>
        <label className={cn("block text-sm font-medium mb-1", text.primary)}>
          <Phone className="w-5 h-4 inline-block mr-1 text-blue-500" />
          {t('contactNumber')} ({t('optional')})
        </label>
        <ThemedInput
          type="tel"
          name="contactNumber"
          value={contactNumber}
          onChange={onChange}
          className="w-full p-2 border rounded-md hover:bg-gray-50"
          placeholder={t('Enter contact number')}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('phoneChargeWarning')}
        </p>
        {/* International phone warning — informational only, does not block booking */}
        {contactNumber && isInternationalNumber(contactNumber) && (
          <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{t('smsInternationalInfo')}</span>
          </div>
        )}
      </div>

      {/* Entity Verification Field - Conditional */}
      {additionalVerificationEnabled && (
        <div>
          <label className={cn("block text-sm font-medium mb-1", text.primary)}>
            <KeyRound className="w-5 h-4 inline-block mr-1 text-blue-500" />
            {t('entityVerificationValueLabel')}
          </label>
          <textarea
            name="entityVerificationValue"
            value={entityVerificationValue}
            onChange={onChange}
            className={cn(
              "w-full p-2 border rounded-md resize-vertical",
              background.primary,
              text.primary,
              "border-gray-200 dark:border-gray-700",
              "hover:bg-gray-50 dark:hover:bg-gray-700",
              "focus:outline-none focus:ring-2 focus:ring-blue-500"
            )}
            placeholder={verificationPrompt || t('entityVerificationValuePlaceholder')}
            maxLength={255}
            rows={3}
          />
          {verificationError && (
            <p className="mt-1 text-sm text-red-600">
              {verificationError}
            </p>
          )}
        </div>
      )}

      {/* Special Requests */}
      <div>
        <label className="block text-sm font-medium mb-1">
          <MessageCircle className="w-5 h-4 inline-block mr-1 text-blue-500" />
          {t('specialRequests')}({t('optional')})
        </label>
        <textarea
          name="specialRequests"
          value={specialRequests}
          onChange={onChange}
          className="w-full p-2 border rounded-md hover:bg-gray-50"
          placeholder={t('Enter any special requests')}
          rows="3"
        />
      </div>
    </>
  );
};


export default React.memo(ContactVerificationSection);
